<script lang="ts">
  /**
   * Розділ «Плеєр» в адмінці: колір, набір кнопок, крок перемотування,
   * швидкості, якість. Поруч — жива панель керування (той самий компонент, що
   * й у плеєрі), тож зміни видно ще до збереження.
   */
  import PlayerControls from './PlayerControls.svelte';
  import { api, ApiError } from '../../lib/api';
  import type { PlayerSettings } from '../../lib/player/settings';
  import type { PlayerActions, PlayerState } from '../../lib/player/types';

  interface Props {
    initial: PlayerSettings;
  }
  let { initial }: Props = $props();

  const ALL_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  const SKIP_STEPS = [5, 10, 15, 30];
  const QUALITIES = [
    { value: 0, label: 'Авто' },
    { value: 720, label: '720p' },
    { value: 1080, label: '1080p' },
  ];
  const CONTROL_LABELS: Record<keyof PlayerSettings['controls'], string> = {
    bigPlay: 'Велика кнопка Play',
    skip: 'Назад / вперед',
    currentTime: 'Поточний час',
    duration: 'Тривалість',
    volume: 'Звук',
    quality: 'Якість',
    speed: 'Швидкість',
    airplay: 'AirPlay',
    pip: 'Картинка в картинці',
    fullscreen: 'Повний екран',
  };
  const controlKeys = Object.keys(CONTROL_LABELS) as (keyof PlayerSettings['controls'])[];
  const DEFAULT_ACCENT = '#B5543A'; // стартове значення палітри, коли вмикають власний колір

  const clone = (s: PlayerSettings): PlayerSettings => ({ ...s, controls: { ...s.controls }, speeds: [...s.speeds] });

  let saved = $state(clone(initial));
  let draft = $state(clone(initial));
  let saving = $state(false);
  let error = $state<string | null>(null);
  let savedAt = $state<number | null>(null);

  const dirty = $derived(JSON.stringify(draft) !== JSON.stringify(saved));

  function toggleSpeed(rate: number): void {
    if (rate === 1) return; // звичайна швидкість є завжди
    draft.speeds = draft.speeds.includes(rate)
      ? draft.speeds.filter((r) => r !== rate)
      : [...draft.speeds, rate].sort((a, b) => a - b);
  }

  async function save(): Promise<void> {
    if (saving) return;
    saving = true;
    error = null;
    try {
      const { player } = await api.adminUpdatePlayerSettings($state.snapshot(draft));
      saved = clone(player);
      draft = clone(player);
      savedAt = Date.now();
    } catch (err) {
      error = err instanceof ApiError && err.status === 400 ? 'Перевір значення' : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }

  // Попередній перегляд: панель без відео. Кнопки натискаються, щоб було видно
  // меню й стани, але нічого не відтворюють.
  const preview = $state<PlayerState>({
    paused: true,
    started: false,
    waiting: false,
    currentTime: 754,
    duration: 3120,
    buffered: 1100,
    muted: false,
    volume: 1,
    rate: 1,
    quality: 0,
    qualities: [1080, 720, 480, 360],
    airplayAvailable: true,
    airplayActive: false,
    pipActive: false,
    fullscreen: false,
    rotated: false,
    failed: false,
  });
  const previewActions: PlayerActions = {
    togglePlay: () => (preview.paused = !preview.paused),
    seek: (sec) => (preview.currentTime = sec),
    skip: (delta) => (preview.currentTime = Math.max(0, Math.min(preview.duration, preview.currentTime + delta))),
    toggleMute: () => (preview.muted = !preview.muted),
    setVolume: (v) => (preview.volume = v),
    setRate: (r) => (preview.rate = r),
    setQuality: (h) => (preview.quality = h),
    airplay: () => (preview.airplayActive = !preview.airplayActive),
    togglePip: () => (preview.pipActive = !preview.pipActive),
    toggleFullscreen: () => {},
    retry: () => {},
  };
</script>

<div class="section-title">Плеєр</div>
<div class="hint">Вигляд і кнопки відеоплеєра для всіх користувачів.</div>

<div class="preview" style:--player-accent={draft.accentColor ?? 'var(--pu)'}>
  <PlayerControls
    state={preview}
    actions={previewActions}
    caps={{ airplay: true, pip: true, volume: true }}
    settings={draft}
  />
</div>
<div class="hint">
  Попередній перегляд. AirPlay і «картинка в картинці» у справжньому плеєрі зʼявляються лише
  на пристроях, які це вміють; на вузькому екрані телефона частина кнопок ховається до
  повного екрана.
</div>

<div class="field-label">Колір акценту</div>
<div class="row">
  <label class="check">
    <input
      type="checkbox"
      checked={draft.accentColor === null}
      onchange={(e) => (draft.accentColor = e.currentTarget.checked ? null : DEFAULT_ACCENT)}
    />
    Фірмовий колір застосунку
  </label>
  {#if draft.accentColor !== null}
    <input
      type="color"
      value={draft.accentColor}
      oninput={(e) => (draft.accentColor = e.currentTarget.value.toUpperCase())}
      aria-label="Колір акценту"
    />
    <span class="hex">{draft.accentColor}</span>
  {/if}
</div>

<div class="field-label">Кнопки</div>
<div class="chips">
  {#each controlKeys as key (key)}
    <button
      type="button"
      class="chip"
      class:on={draft.controls[key]}
      aria-pressed={draft.controls[key]}
      onclick={() => (draft.controls[key] = !draft.controls[key])}
    >
      {CONTROL_LABELS[key]}
    </button>
  {/each}
</div>
<div class="hint">Play / пауза та смуга прогресу є завжди.</div>

<div class="field-label">Крок перемотування</div>
<div class="chips">
  {#each SKIP_STEPS as step (step)}
    <button type="button" class="chip" class:on={draft.skipSeconds === step} onclick={() => (draft.skipSeconds = step)}>
      {step} с
    </button>
  {/each}
</div>
<div class="hint">Для кнопок «назад / вперед» і подвійного дотику по краях відео.</div>

<div class="field-label">Швидкості в меню</div>
<div class="chips">
  {#each ALL_SPEEDS as rate (rate)}
    <button
      type="button"
      class="chip"
      class:on={draft.speeds.includes(rate)}
      disabled={rate === 1}
      onclick={() => toggleSpeed(rate)}
    >
      {rate}×
    </button>
  {/each}
</div>

<div class="field-label">Якість за замовчуванням</div>
<div class="chips">
  {#each QUALITIES as q (q.value)}
    <button
      type="button"
      class="chip"
      class:on={draft.defaultQuality === q.value}
      onclick={() => (draft.defaultQuality = q.value)}
    >
      {q.label}
    </button>
  {/each}
</div>
<div class="hint">
  «Авто» підлаштовується під швидкість інтернету. Фіксована якість стартує одразу чіткою,
  але на слабкому звʼязку відео може підвисати.
</div>

<label class="check standalone">
  <input type="checkbox" bind:checked={draft.rememberChoices} />
  Памʼятати на пристрої вибрані якість і швидкість
</label>

{#if error}
  <div class="err">{error}</div>
{/if}

<button type="button" class="submit" disabled={!dirty || saving} onclick={save}>
  {saving ? 'Зберігаємо…' : 'Зберегти плеєр'}
</button>
{#if savedAt !== null && !dirty}
  {#key savedAt}
    <div class="saved-note">Збережено</div>
  {/key}
{/if}

<style>
  .section-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--text);
    margin: 32px 0 2px;
  }
  .field-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    margin: 14px 0 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .hint {
    font-size: 11px;
    color: var(--text-muted);
    margin: 4px 2px 0;
    line-height: 1.4;
  }
  .preview {
    position: relative;
    aspect-ratio: 16 / 9;
    margin-top: 10px;
    border-radius: 12px;
    overflow: hidden;
    background: linear-gradient(135deg, #3a3340, #17131b);
  }
  .row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  .check {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text);
  }
  .check.standalone {
    margin-top: 16px;
  }
  .hex {
    font-size: 12px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
  input[type='color'] {
    width: 40px;
    height: 30px;
    padding: 0;
    border: 1px solid var(--border2);
    border-radius: 8px;
    background: none;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    padding: 7px 12px;
    border: 1px solid var(--border2);
    border-radius: 999px;
    background: var(--bg2);
    color: var(--text-muted);
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }
  .chip.on {
    border-color: var(--pu);
    background: var(--pu);
    color: var(--text-light);
  }
  .chip:disabled {
    cursor: default;
    opacity: 0.7;
  }
  .err {
    margin: 14px 0 0;
    font-size: 12px;
    color: var(--status-inactive);
  }
  .submit {
    width: 100%;
    margin-top: 18px;
    padding: 14px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .submit:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .saved-note {
    margin-top: 10px;
    text-align: center;
    font-size: 12px;
    color: var(--status-active);
  }
</style>
